import { useMutation, useQuery } from "@tanstack/react-query";

import {
  createBussiness,
  createForm,
  excelExport,
  generateMarketerSettlement,
  getAvailableBalance,
  getBankCardDetails,
  getBussinessBalance,
  getBussinessData,
  getBussinessStatistics,
  getExports,
  getForm,
  getFormDataByLink,
  getFormList,
  getGatewayList,
  getIbanDetails,
  getMarketerReferedList,
  getMarketerSettlements,
  getMessages,
  getNotifications,
  getNotificationSetting,
  getPortData,
  getPosPosTerminals,
  getSettlementsList,
  getSidebarBusinessList,
  getTicketsCategoryList,
  getTicketsList,
  getTicketsPriorityList,
  getTransactionList,
  getUserApprovedAccounts,
  getUserBusinessList,
  getUserTicketData,
  sendEmail,
  submitGateway,
  submitPurchaseForm,
  submitTicket,
  updateGatewayPin,
  updateNotificationSetting,
  updateTransactionVerified,
  verifyEmail,
} from "../api/api";

export const useSendEmail = () => {
  return useMutation({
    mutationFn: (email) => sendEmail(email),
  });
};
export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: (email, otp) => verifyEmail(email, otp),
  });
};
export const useCreateBussiness = () => {
  return useMutation({
    mutationFn: ({ data, type }) => createBussiness({ data, type }),
  });
};
export const useCreateForm = () => {
  return useMutation({
    mutationFn: (data) => createForm(data),
  });
};
export const useSubmitPurchaseForm = () => {
  return useMutation({
    mutationFn: (data) => submitPurchaseForm(data),
  });
};

export const useGetUserTicketData = () => {
  return useQuery({
    queryKey: ["getUserTicketData"],
    queryFn: getUserTicketData,
  });
};

export const useGetUserApprovedAccounts = () => {
  return useQuery({
    queryKey: ["getUserApprovedAccounts"],
    queryFn: getUserApprovedAccounts,
  });
};

export const useGetSidebarBusinessList = ({ currentPage, pageSize, userIsValid }) => {
  return useQuery({
    queryKey: ["getSidebarBusinessList"],
    queryFn: () => getSidebarBusinessList({ currentPage, pageSize }),
  });
};
export const useGetUserBusinessList = ({ currentPage, pageSize }) => {
  return useQuery({
    queryKey: ["getUserBusinessList", currentPage],
    queryFn: () => getUserBusinessList({ currentPage, pageSize }),
    enabled: false,
  });
};

export const useGetTicketsCategoryList = () => {
  return useQuery({
    queryKey: ["getTicketsCategoryList"],
    queryFn: getTicketsCategoryList,
  });
};
export const useGetTicketsPriorityList = () => {
  return useQuery({
    queryKey: ["getTicketsPriorityList"],
    queryFn: getTicketsPriorityList,
  });
};

export const useSubmitTicket = () => {
  return useMutation({
    mutationFn: (body) => submitTicket(body),
  });
};
export const useSubmitGateway = () => {
  return useMutation({
    mutationFn: (body) => submitGateway(body),
  });
};
export const useUpdateGatewayPin = () => {
  return useMutation({
    mutationFn: (ticket_id) => updateGatewayPin(ticket_id),
  });
};
export const useUpdateTransactionVerified = () => {
  return useMutation({
    mutationFn: (id) => updateTransactionVerified(id),
  });
};

export const useGetBussinessData = (type, id, dispatch) => {
  return useQuery({
    queryKey: ["bussinessData"],
    queryFn: () => getBussinessData(type, id, dispatch),
    enabled: false,
  });
};
export const useGetPortData = (id) => {
  return useQuery({
    queryKey: ["portData", id],
    queryFn: () => getPortData(id),
  });
};
export const useGetFormDataByLink = (linkId) => {
  return useQuery({
    queryKey: ["portData", linkId],
    queryFn: () => getFormDataByLink(linkId),
  });
};
export const useGetBussinessStatistics = (business_type, business_id, time_frame) => {
  return useQuery({
    queryKey: ["bussinessStatistics "],
    queryFn: () => getBussinessStatistics(business_type, business_id, time_frame),
    enabled: false,
  });
};
export const useGetBussinessBalance = (business_type, business_id) => {
  return useQuery({
    queryKey: ["getBussinessBalance "],
    queryFn: () => getBussinessBalance(business_type, business_id),
  });
};
export const useGetAvailableBalance = ({ wallet_id }) => {
  return useQuery({
    queryKey: ["bussinessData"],
    queryFn: () => getAvailableBalance({ wallet_id }),
    enabled: !!wallet_id,
  });
};

export const useGetTransactionList = ({
  type,
  transactionType,
  businessId,
  searchTerm,
  currentPage,
  pageSize,
}) => {
  return useQuery({
    queryFn: () =>
      getTransactionList({
        type,
        businessId,
        searchTerm,
        currentPage,
        pageSize,
        transactionType,
      }),
    queryKey: ["getTransactionList"],
    enabled: false,
  });
};

export const useGetSettlementsList = (searchTerm, currentPage, pageSize) => {
  return useQuery({
    queryFn: () => getSettlementsList(searchTerm, currentPage, pageSize),
    queryKey: ["getSettlementsList"],
  });
};

export const useExcelExport = () => {
  return useMutation({
    mutationFn: ({ data, searchTermRef, url, type, businessId }) =>
      excelExport({ data, searchTermRef, url, type, businessId }),
  });
};

export const useGetIbanDetails = () => {
  return useMutation({
    mutationFn: (iban) => getIbanDetails(iban),
  });
};

export const useGetBankCardDetails = () => {
  return useMutation({
    mutationFn: (card_number) => getBankCardDetails(card_number),
  });
};
export const useGetPosTerminals = (searchTerm, currentPage, pageSize) => {
  return useQuery({
    queryFn: () => getPosPosTerminals(searchTerm, currentPage, pageSize),
    enabled: false,
  });
};

export const useGetGatewayList = ({ type, businessId, currentPage, pageSize }) => {
  return useQuery({
    queryFn: () =>
      getGatewayList({
        type,
        businessId,
        currentPage,
        pageSize,
      }),
    queryKey: ["getGatewayList", currentPage],
  });
};
export const useGetFormList = ({ type, businessId, currentPage, pageSize }) => {
  return useQuery({
    queryFn: () =>
      getFormList({
        type,
        businessId,
        currentPage,
        pageSize,
      }),
    queryKey: ["getFormList", currentPage],
  });
};

export const useGetTicketsList = ({ currentPage, pageSize }) => {
  return useQuery({
    queryFn: () =>
      getTicketsList({
        currentPage,
        pageSize,
      }),
    queryKey: ["getTicketsList", currentPage],
  });
};
export const useGetExports = ({ currentPage, pageSize }) => {
  return useQuery({
    queryFn: () =>
      getExports({
        currentPage,
        pageSize,
      }),
    queryKey: ["getExports", currentPage],
  });
};

export const useGetForm = () => {
  return useMutation({
    mutationFn: (id) => getForm(id),
  });
};

export const useGetMessages = ({ userId, currentPage, pageSize }) => {
  return useQuery({
    queryFn: () =>
      getMessages({
        userId,
        currentPage,
        pageSize,
      }),
    queryKey: ["getMessagesList"],
  });
};
export const useGetNotification = ({ currentPage, pageSize }) => {
  return useQuery({
    queryFn: () =>
      getNotifications({
        currentPage,
        pageSize,
      }),
    queryKey: ["getNotificationList"],
  });
};

export const useGetNotificationSetting = (userId) => {
  return useQuery({
    queryFn: () => getNotificationSetting(userId),
  });
};
export const useUpdateNotificationSetting = () => {
  return useMutation({
    mutationFn: ({ userId, updatedData }) => updateNotificationSetting({ userId, updatedData }),
  });
};

export const useGetMarketerReferedList = ({ currentPage, pageSize }) => {
  return useQuery({
    queryFn: () =>
      getMarketerReferedList({
        currentPage,
        pageSize,
      }),
    queryKey: ["useGetMarketerReferedList", currentPage],
  });
};
export const useGetMarketerSettlements = ({ currentPage, pageSize }) => {
  return useQuery({
    queryFn: () =>
      getMarketerSettlements({
        currentPage,
        pageSize,
      }),
    queryKey: ["getMarketerSettlements", currentPage],
  });
};

export const useGenerateMarketerSettlement = () => {
  return useMutation({
    mutationFn: (data) => generateMarketerSettlement(data),
  });
};
